import { aiClipCreditsFor } from 'fym-shared/src/music24/music24Plans';
import {
  DIRECTED_CLIP_SEC,
  directedClips,
  directedFootageSec,
  directedRefundCredits,
  type SerializedDirectedVideo,
} from 'fym-shared/src/music24/directedVideoApi';
import { MUSIC_VIDEO_EFFECTS, type MusicVideoEffectFamily } from 'fym-shared/src/music24/musicVideoEffects';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { EFFECT_FAMILY_ORDER } from '../director/familyMessages';

type ResultVideo = Pick<SerializedDirectedVideo, 'script' | 'clips' | 'clipEntries' | 'priceCredits' | 'asset'>;

/**
 * The numbers of the finished video (design directed-video k1, k2). Two counts that the old ready
 * card mixed up (owner report 2026-10-18, "34 shots" next to "30 new shots"):
 * - `shots`: the shots of the script, the cuts of the video (`script.shots`).
 * - `clipsMade`: the new AI clips that the make made. One clip can hold several shots (the shots
 *   with the same `clipKey`), so there are as many or more shots than clips.
 */
export interface DirectedResultFacts {
  shots: number;
  clipsMade: number;
  clipsFailed: number;
  effects: number;
  /** The effect families of the script, in the order of the design. */
  families: MusicVideoEffectFamily[];
  /** The seconds of new footage of the make: the lengths of the clips of the script added up. */
  footageSec: number;
  /** The seconds of new footage of the clips that the make made (no failed clip). */
  madeFootageSec: number;
  /** The credits back for the failed clips. */
  backCredits: number;
  /** The price minus the credits back. */
  chargedCredits: number;
}

/** One clip of the make: its length, and true when it failed for good. */
interface MadeClip {
  clipSec: number;
  failed: boolean;
}

/**
 * The clips of the make with their lengths. The length of a clip comes from the script
 * (`directedClips`, by `clipKey`); its state from `clipEntries`. An API from before 2026-10-18 sends
 * no entries, and entries that do not agree with `clips` are not used: then every clip is 8 s (every
 * make before 2026-10-23) and `clips.failed` of them failed. Pure.
 */
function madeClipsOf(video: ResultVideo): MadeClip[] {
  const { total, failed } = video.clips;
  const entries = video.clipEntries ?? [];
  const failedEntries = entries.filter((entry) => entry.status === 'failed').length;
  if (entries.length !== total || failedEntries !== failed) {
    return Array.from({ length: Math.max(0, total) }, (_, index) => ({
      clipSec: DIRECTED_CLIP_SEC,
      failed: index < failed,
    }));
  }
  const lengths = new Map(
    (video.script === null ? [] : directedClips(video.script)).map((clip) => [clip.clipKey, clip.clipSec])
  );
  return entries.map((entry) => ({
    clipSec: lengths.get(entry.clipKey) ?? DIRECTED_CLIP_SEC,
    failed: entry.status === 'failed',
  }));
}

/**
 * The numbers of the finished video. The credits back are the share of the price of each failed
 * clip by its seconds (`directedRefundCredits`, as the server refunds): a make of 31 clips of 8 s for
 * 124 credits gives 4 back for each failed clip. Pure.
 */
export function directedResultFacts(video: ResultVideo): DirectedResultFacts {
  const { total, failed } = video.clips;
  const clips = madeClipsOf(video);
  const price = video.priceCredits ?? clips.reduce((sum, clip) => sum + aiClipCreditsFor(clip.clipSec), 0);
  const backCredits = directedRefundCredits(video.priceCredits, clips);
  const effects = video.script?.effects ?? [];
  const present = new Set(effects.map((effect) => MUSIC_VIDEO_EFFECTS[effect.kind].family));
  return {
    shots: video.script?.shots.length ?? 0,
    clipsMade: Math.max(0, total - failed),
    clipsFailed: failed,
    effects: effects.length,
    families: EFFECT_FAMILY_ORDER.filter((family) => present.has(family)),
    footageSec:
      video.script === null ? clips.reduce((sum, clip) => sum + clip.clipSec, 0) : directedFootageSec(video.script),
    madeFootageSec: clips.reduce((sum, clip) => sum + (clip.failed ? 0 : clip.clipSec), 0),
    backCredits,
    chargedCredits: Math.max(0, price - backCredits),
  };
}

/** The signed stills of the ready clips, in script order (design k1 strip). Pure. */
export function directedResultStills(video: Pick<SerializedDirectedVideo, 'clipEntries'>): string[] {
  return (video.clipEntries ?? []).flatMap((entry) =>
    entry.status === 'ready' && entry.stillUrl !== null ? [entry.stillUrl] : []
  );
}

/**
 * The poster of the player: the poster frame of the render (an API path, made full by
 * `resolveApiFile`), else the still of the first ready clip. Never the cover art of the song. Pure.
 */
export function directedResultPoster(
  video: Pick<SerializedDirectedVideo, 'asset' | 'clipEntries'>,
  resolveApiFile: (path: string) => string
): string | null {
  const posterUrl = video.asset?.posterUrl ?? null;
  if (posterUrl !== null) {
    return resolveApiFile(posterUrl);
  }
  return directedResultStills(video)[0] ?? null;
}

/** The file that the player plays: the rendered file of a ready render, else null. Pure. */
export function directedResultFile(video: Pick<SerializedDirectedVideo, 'asset'>): string | null {
  const asset = video.asset ?? null;
  return asset !== null && asset.status === 'ready' ? asset.fileUrl : null;
}

/**
 * The in-app link of the asset viewer of the render: the `promoteWork?result=` link of the "asset is
 * ready" email (`workAssetUrl` of music24-api, without the `/app` prefix). The web redirect opens
 * the file in its viewer, where Download, Share and Post live. Pure.
 */
export function directedAssetViewerPath(catalogItemUuid: string, accountId: number, assetUuid: string): string {
  const query = new URLSearchParams({ catalogItemUuid, result: assetUuid, accountId: String(accountId) });
  return `/${SMLRouteNames.promote}/${SMLRouteNames.promoteWork}?${query.toString()}`;
}
